import React, { useEffect, useState } from 'react';
import { Pressable, ScrollView, View } from 'react-native';

import { Button, Modal, Text, TextInput, useTheme } from '@/components/ui';
import type { ApplicationChecklistItem, MessageAttachment } from '@/services/data-service';

/**
 * Re-file a photo (#369).
 *
 * Homeowners upload into whatever form field is nearest — driveway photos arrive under
 * "Literature" because that was the box on the page. The classifier honours what the form
 * said, which is right at ingest and wrong five minutes later once a human has looked.
 *
 * So this does two separate things, and the second is the one that matters: change what a
 * file IS (its type and caption), and say which REQUIREMENT it answers. Assigning a file to
 * "Photos of the cameras showing their design" is the reviewer's actual judgement, and it
 * is what lets the checklist be derived from evidence rather than from ticked boxes.
 */

const TYPES: Array<{ value: string; label: string }> = [
	{ value: 'photo', label: 'Photo' },
	{ value: 'literature', label: 'Product Literature' },
	{ value: 'plan', label: 'Plan / Drawing' },
	{ value: 'quote', label: 'Quote / Contract' },
	{ value: 'insurance', label: 'Insurance' },
	{ value: 'receipt', label: 'Receipt' },
	{ value: 'invoice', label: 'Invoice' },
	{ value: 'misc', label: 'Other' },
];

export interface AttachmentMetadataModalProps {
	attachment: MessageAttachment | null;
	checklist: ApplicationChecklistItem[];
	busy?: boolean;
	onDismiss: () => void;
	onSave: (values: { attachmentType: string; description: string | null; checklistKey: string | null }) => void;
}

const Chip: React.FC<{ label: string; selected: boolean; onPress: () => void; testID: string }> = ({
	label, selected, onPress, testID,
}) => {
	const theme = useTheme();
	return (
		<Pressable
			onPress={onPress}
			testID={testID}
			style={{
				paddingVertical: 7, paddingHorizontal: 12,
				borderRadius: 16, marginRight: 6, marginBottom: 6,
				borderWidth: 1,
				borderColor: selected ? theme.colors.primary : (theme.colors.outlineVariant ?? theme.colors.outline),
				backgroundColor: selected ? theme.colors.primaryContainer : 'transparent',
			}}
		>
			<Text style={{ fontSize: 13, fontWeight: selected ? '700' : '400', color: theme.colors.onSurface }}>
				{label}
			</Text>
		</Pressable>
	);
};

export const AttachmentMetadataModal: React.FC<AttachmentMetadataModalProps> = ({
	attachment, checklist, busy = false, onDismiss, onSave,
}) => {
	const theme = useTheme();
	const [type, setType] = useState('misc');
	const [label, setLabel] = useState('');
	const [checklistKey, setChecklistKey] = useState<string | null>(null);

	// Re-seed whenever a DIFFERENT file is opened, or the form shows the last one's values.
	useEffect(() => {
		if (!attachment) return;
		setType(attachment.attachmentType || 'misc');
		setLabel(attachment.label ?? '');
		setChecklistKey(attachment.checklistKey ?? null);
	}, [attachment]);

	return (
		<Modal visible={attachment != null} onDismiss={onDismiss}>
			<View style={{ padding: 20, width: 460, maxWidth: '100%' }}>
				<Text style={{ fontSize: 18, fontWeight: '700' }}>Edit file details</Text>
				<Text numberOfLines={1} style={{ color: theme.colors.onSurfaceVariant, marginBottom: 14 }}>
					{attachment?.fileName}
				</Text>

				<ScrollView style={{ maxHeight: 420 }}>
					<Text style={{ fontWeight: '700', marginBottom: 6 }}>Type</Text>
					<View style={{ flexDirection: 'row', flexWrap: 'wrap' }}>
						{TYPES.map((option) => (
							<Chip
								key={option.value}
								label={option.label}
								selected={type === option.value}
								onPress={() => setType(option.value)}
								testID={`attachment-type-${option.value}`}
							/>
						))}
					</View>

					<Text style={{ fontWeight: '700', marginTop: 14, marginBottom: 4 }}>Caption</Text>
					<TextInput
						value={label}
						onChangeText={setLabel}
						placeholder="What this shows"
						testID="attachment-label-input"
					/>

					{checklist.length > 0 ? (
						<>
							<Text style={{ fontWeight: '700', marginTop: 16, marginBottom: 2 }}>Answers requirement</Text>
							<Text style={{ fontSize: 12, color: theme.colors.onSurfaceVariant, marginBottom: 6 }}>
								Ticks that item off the submission requirements.
							</Text>
							<Chip
								label="None"
								selected={checklistKey == null}
								onPress={() => setChecklistKey(null)}
								testID="checklist-key-none"
							/>
							{checklist.map((item) => (
								<Chip
									key={item.key}
									label={item.label}
									selected={checklistKey === item.key}
									onPress={() => setChecklistKey(item.key)}
									testID={`checklist-key-${item.key}`}
								/>
							))}
						</>
					) : null}
				</ScrollView>

				<View style={{ flexDirection: 'row', justifyContent: 'flex-end', gap: 8, marginTop: 16 }}>
					<Button mode="outlined" onPress={onDismiss}>Cancel</Button>
					<Button
						mode="contained"
						loading={busy}
						disabled={busy}
						testID="btn-save-attachment-metadata"
						onPress={() => onSave({
							attachmentType: type,
							description: label.trim() || null,
							checklistKey,
						})}
					>
						Save
					</Button>
				</View>
			</View>
		</Modal>
	);
};

export default AttachmentMetadataModal;
